import { useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { ShieldCheck, Key, Copy, Check, QrCode } from '@phosphor-icons/react';
import api from '@/lib/api';

// Invite codes are 8 chars from an unambiguous alphabet; accept them typed
// however they arrive (lowercase, grouped with a dash, padded with spaces).
const CODE_ALPHABET = /[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]/;
const normalizeCode = (raw) => (raw || '').toUpperCase().split('').filter(c => CODE_ALPHABET.test(c)).join('');
const groupCode = (c) => (c.length > 4 ? `${c.slice(0, 4)}-${c.slice(4, 8)}` : c);

export default function SetupPage() {
  const { status, login } = useAuth();
  const { t } = useI18n();
  // An already-initialised system can't be bootstrapped again, so a returning
  // device starts on the invite-code path rather than the raw-token one.
  const [mode, setMode] = useState(status === 'setup' ? 'setup' : 'code');
  const [deviceName, setDeviceName] = useState('');
  const [token, setToken] = useState('');
  const [code, setCode] = useState('');
  const [generatedToken, setGeneratedToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Arriving from a scanned QR (/join?code=…): prefill and switch to the code tab.
  useEffect(() => {
    const fromUrl = normalizeCode(new URLSearchParams(window.location.search).get('code') || '');
    if (fromUrl) { setCode(fromUrl); setMode('code'); }
  }, []);

  const handleRedeem = async (raw) => {
    const c = normalizeCode(raw ?? code);
    if (c.length !== 8) return;
    setLoading(true);
    try {
      const res = await api.post('/invites/redeem', {
        code: c,
        ...(deviceName.trim() ? { device_name: deviceName.trim() } : {}),
      });
      // Clear the code from the URL so a bookmark or back-button doesn't retry
      // a now-spent invite and show a confusing error.
      window.history.replaceState({}, '', window.location.pathname);
      toast.success(t('invite.welcome', { name: res.data.user?.name || '' }));
      login(res.data.token);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('invite.redeemFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async () => {
    if (!deviceName.trim()) return;
    setLoading(true);
    try {
      const res = await api.post('/bootstrap/register-device', { name: deviceName.trim() });
      setGeneratedToken(res.data.token);
      toast.success(t('auth.tokenSaved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error registering device');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedToken);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleConnect = () => {
    if (generatedToken) {
      login(generatedToken);
    } else if (token.trim()) {
      login(token.trim());
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background" data-testid="setup-page">
      <Card className="w-full max-w-md animate-fade-in">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-2">
            <ShieldCheck size={28} className="text-primary" weight="duotone" />
          </div>
          <CardTitle className="font-heading text-2xl tracking-tight">
            {mode === 'setup' ? t('auth.welcome') : mode === 'code' ? t('invite.joinTitle') : t('auth.enterToken')}
          </CardTitle>
          <CardDescription>
            {mode === 'setup' ? t('auth.setupDesc') : mode === 'code' ? t('invite.joinDesc') : t('auth.tokenDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {mode === 'setup' && !generatedToken && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('auth.deviceName')}</label>
                <Input
                  data-testid="device-name-input"
                  placeholder="e.g. MacBook Pro"
                  value={deviceName}
                  onChange={(e) => setDeviceName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSetup()}
                />
              </div>
              <Button data-testid="register-device-btn" className="w-full" onClick={handleSetup} disabled={loading || !deviceName.trim()}>
                {loading ? t('common.loading') : t('auth.register')}
              </Button>
              <button data-testid="switch-to-token-from-setup" className="text-sm text-muted-foreground hover:text-foreground w-full text-center transition-colors" onClick={() => setMode('token')}>
                {t('auth.haveToken')}
              </button>
            </>
          )}

          {mode === 'setup' && generatedToken && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium text-warning">{t('settings.tokenWarning')}</label>
                <div className="relative">
                  <Input data-testid="generated-token" value={generatedToken} readOnly className="pr-10 font-mono text-xs" />
                  <button data-testid="copy-token-btn" className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={handleCopy}>
                    {copied ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
                  </button>
                </div>
              </div>
              <Button data-testid="connect-btn" className="w-full" onClick={handleConnect}>
                {t('auth.connect')}
              </Button>
            </>
          )}

          {mode === 'code' && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="invite-code-input">{t('invite.codeLabel')}</label>
                <div className="relative">
                  <QrCode size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="invite-code-input"
                    data-testid="invite-code-input"
                    placeholder="ABCD-2345"
                    value={groupCode(code)}
                    onChange={(e) => setCode(normalizeCode(e.target.value).slice(0, 8))}
                    onKeyDown={(e) => e.key === 'Enter' && handleRedeem()}
                    autoComplete="one-time-code"
                    autoFocus
                    className="pl-9 font-mono text-lg tracking-[0.2em] text-center"
                  />
                </div>
              </div>
              <Button
                data-testid="redeem-code-btn"
                className="w-full"
                onClick={() => handleRedeem()}
                disabled={loading || normalizeCode(code).length !== 8}
              >
                {loading ? t('common.loading') : t('invite.join')}
              </Button>
              <button
                data-testid="switch-to-token-from-code"
                className="text-sm text-muted-foreground hover:text-foreground w-full text-center transition-colors"
                onClick={() => setMode('token')}
              >
                {t('invite.haveTokenInstead')}
              </button>
            </>
          )}

          {mode === 'token' && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('auth.token')}</label>
                <div className="relative">
                  <Key size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    data-testid="token-input"
                    placeholder={t('auth.pasteToken')}
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleConnect()}
                    className="pl-9 font-mono text-xs"
                  />
                </div>
              </div>
              <Button data-testid="token-connect-btn" className="w-full" onClick={handleConnect} disabled={!token.trim()}>
                {t('auth.connect')}
              </Button>
              {/* Only offer bootstrap when the system genuinely has no devices.
                  On an initialised system it returns 403 "System already
                  initialized" as a raw English toast — a dead end that looked
                  like the intended path for a new family member. They want the
                  invite-code flow instead. */}
              {status === 'setup' ? (
                <button data-testid="switch-to-setup" className="text-sm text-muted-foreground hover:text-foreground w-full text-center transition-colors" onClick={() => setMode('setup')}>
                  {t('auth.needSetup')}
                </button>
              ) : (
                <button data-testid="switch-to-code-from-token" className="text-sm text-muted-foreground hover:text-foreground w-full text-center transition-colors" onClick={() => setMode('code')}>
                  {t('invite.haveCodeInstead')}
                </button>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
